// React-query hooks for the metaphysics document-sets API.
//
// Query key conventions:
//   ['document-sets']                 list
//   ['document-set', setId]           single set
//   ['document-set-versions', setId]  every version (DRAFT + COMMITTED)
//   ['document-set-version', setId, 'latest' | name]  one committed version + manifest

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import {
  encodeSplatPath,
  useMetaphysics,
  type IDocumentSet,
  type IDocumentSetExtractionSchema,
  type IDocumentSetExtractionSchemaGeneratedFrom,
  type IDocumentSetKgChangelog,
  type IDocumentSetKgProvenance,
  type IDocumentSetKgSyncJobWithItems,
  type IDocumentSetKgVersion,
  type IDocumentSetVersion,
  type IEntryDetail,
  type IGenerateKgRequest,
  type IGenerateKgResponse,
  type ILineageResponse,
  type IManifestEntry,
  type ISkillRun,
  type IUploadProgress,
  type IVersionDiff,
  type IVersionWithManifest,
} from '../lib/metaphysics';

// ── Read hooks ─────────────────────────────────────────────────────────────
export function useDocumentSets() {
  const meta = useMetaphysics();
  return useQuery({
    queryKey: ['document-sets'] as const,
    queryFn: () => meta.get('document-sets').json<{ items: IDocumentSet[] }>(),
    select: (raw) => raw.items,
  });
}

export function useDocumentSet(setId: string | undefined) {
  const meta = useMetaphysics();
  return useQuery({
    queryKey: ['document-set', setId] as const,
    enabled: !!setId,
    queryFn: () =>
      meta.get(`document-sets/${encodeURIComponent(setId as string)}`).json<IDocumentSet>(),
  });
}

export function useDocumentSetVersions(setId: string | undefined) {
  const meta = useMetaphysics();
  return useQuery({
    queryKey: ['document-set-versions', setId] as const,
    enabled: !!setId,
    queryFn: () =>
      meta
        .get(`document-sets/${encodeURIComponent(setId as string)}/versions`)
        .json<{ items: IDocumentSetVersion[] }>(),
    select: (raw) => raw.items,
  });
}

/**
 * Latest committed version + its manifest. Returns `null` (not error) when
 * the set has no committed versions yet — that's a normal "empty set" state
 * for the UI rather than something to surface as a failure.
 */
export function useLatestVersion(setId: string | undefined) {
  const meta = useMetaphysics();
  return useQuery({
    queryKey: ['document-set-version', setId, 'latest'] as const,
    enabled: !!setId,
    queryFn: async (): Promise<IVersionWithManifest | null> => {
      try {
        return await meta
          .get(`document-sets/${encodeURIComponent(setId as string)}/versions/latest`)
          .json<IVersionWithManifest>();
      } catch (err) {
        // Service throws NOT_FOUND when no committed version exists; surface
        // that as a benign null so the UI can render an "empty" baseline.
        if ((err as Error).message.includes('→ 404')) return null;
        throw err;
      }
    },
  });
}

export function useVersionByName(setId: string | undefined, name: string | undefined) {
  const meta = useMetaphysics();
  return useQuery({
    queryKey: ['document-set-version', setId, name] as const,
    enabled: !!setId && !!name,
    queryFn: () =>
      meta
        .get(
          `document-sets/${encodeURIComponent(setId as string)}/versions/${encodeURIComponent(
            name as string,
          )}`,
        )
        .json<IVersionWithManifest>(),
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────
export function useCreateDocumentSet() {
  const meta = useMetaphysics();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; description?: string }) =>
      meta.post('document-sets', { json: body }).json<IDocumentSet>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['document-sets'] }),
  });
}

export function useDeleteDocumentSet() {
  const meta = useMetaphysics();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (setId: string) => meta.delete(`document-sets/${encodeURIComponent(setId)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['document-sets'] }),
  });
}

export function useCreateDraft(setId: string) {
  const meta = useMetaphysics();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { forkFromVersionId?: string }) =>
      meta
        .post(`document-sets/${encodeURIComponent(setId)}/drafts`, { json: body })
        .json<IDocumentSetVersion>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['document-set-versions', setId] }),
  });
}

export function useCommitDraft(setId: string) {
  const meta = useMetaphysics();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      draftId,
      name,
      description,
    }: {
      draftId: string;
      name: string;
      description?: string;
    }) =>
      meta
        .post(
          `document-sets/${encodeURIComponent(setId)}/drafts/${encodeURIComponent(draftId)}/commit`,
          {
            json: { name, description },
          },
        )
        .json<IDocumentSetVersion>(),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['document-set-versions', setId] }),
        qc.invalidateQueries({ queryKey: ['document-set-version', setId, 'latest'] }),
        qc.invalidateQueries({ queryKey: ['document-sets'] }),
      ]);
    },
  });
}

export function useRemoveDraftFile(setId: string, draftId: string | undefined) {
  const meta = useMetaphysics();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (path: string) => {
      if (!draftId) throw new Error('No active draft');
      const splat = encodeSplatPath(path);
      try {
        return await meta.delete(
          `document-sets/${encodeURIComponent(setId)}/drafts/${encodeURIComponent(draftId)}/files/${splat}`,
        );
      } catch (err) {
        // A 404 means the path isn't a materialized entry in this draft (e.g. a
        // baseline-only file) — the desired end state (gone) already holds, so
        // treat it as success rather than surfacing a scary error.
        if (err instanceof Error && /\b404\b/.test(err.message)) return undefined;
        throw err;
      }
    },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['document-set-versions', setId] }),
        qc.invalidateQueries({ queryKey: ['document-set-lineage', setId] }),
      ]),
  });
}

/**
 * Imperative file-upload helper. Not a `useMutation` because react-query's
 * single-flight model doesn't compose well with N parallel uploads each
 * needing per-file progress state. Returns a stable callback the caller
 * invokes per file; tracking per-path progress is the caller's job.
 */
export function useUploadDraftFile(setId: string, draftId: string | undefined) {
  const meta = useMetaphysics();
  return useCallback(
    async (
      path: string,
      file: File | Blob,
      onProgress?: (p: IUploadProgress) => void,
      signal?: AbortSignal,
    ): Promise<IManifestEntry> => {
      if (!draftId) throw new Error('No active draft');
      return meta.uploadFile({ setId, draftId, path, file, onProgress, signal });
    },
    [meta, setId, draftId],
  );
}

// ── DocumentSet → KG (direct path) ────────────────────────────────────────
//
// Query key conventions:
//   ['document-set-kg-versions', setId]                 list of KG versions
//   ['document-set-kg-version', setId, kgVersion]       single KG version row
//   ['document-set-kg-changelog', setId, kgVersion]     changelog for a KG version
//   ['document-set-kg-provenance', setId, kgVersion]    provenance for a KG version
//   ['document-set-kg-job', setId, jobId]               job + items (progress polling)

export function useDocumentSetKgVersions(setId: string | undefined) {
  const meta = useMetaphysics();
  return useQuery({
    queryKey: ['document-set-kg-versions', setId] as const,
    enabled: !!setId,
    queryFn: () =>
      meta
        .get(`document-sets/${encodeURIComponent(setId as string)}/kg/versions`)
        .json<{ items: IDocumentSetKgVersion[] }>(),
    select: (raw) => raw.items,
    // Poll while the latest KG version's sync is still in-flight so the
    // status/button on the KG tab reflect the transition to a terminal
    // state without requiring an active progress modal. Once the latest
    // row lands in a terminal state, `refetchInterval` returns `false`
    // and polling stops.
    refetchInterval: (q) => {
      const latest = q.state.data?.items?.[0];
      const s = latest?.syncJobState;
      const inFlight = s === 'pending' || s === 'converting' || s === 'pushing' || s === 'indexing';
      return inFlight ? 5_000 : false;
    },
  });
}

export function useDocumentSetKgVersion(setId: string | undefined, kgVersion: number | undefined) {
  const meta = useMetaphysics();
  return useQuery({
    queryKey: ['document-set-kg-version', setId, kgVersion] as const,
    enabled: !!setId && typeof kgVersion === 'number',
    queryFn: () =>
      meta
        .get(`document-sets/${encodeURIComponent(setId as string)}/kg/versions/${kgVersion}`)
        .json<IDocumentSetKgVersion>(),
  });
}

export function useDocumentSetKgChangelog(
  setId: string | undefined,
  kgVersion: number | undefined,
) {
  const meta = useMetaphysics();
  return useQuery({
    queryKey: ['document-set-kg-changelog', setId, kgVersion] as const,
    enabled: !!setId && typeof kgVersion === 'number',
    queryFn: () =>
      meta
        .get(
          `document-sets/${encodeURIComponent(setId as string)}/kg/versions/${kgVersion}/changelog`,
        )
        .json<IDocumentSetKgChangelog>(),
  });
}

export function useDocumentSetKgProvenance(
  setId: string | undefined,
  kgVersion: number | undefined,
) {
  const meta = useMetaphysics();
  return useQuery({
    queryKey: ['document-set-kg-provenance', setId, kgVersion] as const,
    enabled: !!setId && typeof kgVersion === 'number',
    queryFn: () =>
      meta
        .get(
          `document-sets/${encodeURIComponent(setId as string)}/kg/versions/${kgVersion}/provenance`,
        )
        .json<IDocumentSetKgProvenance>(),
  });
}

/**
 * Poll a KG sync job's state + per-item progress. The caller controls
 * cadence via `refetchInterval` — typically 2s while the modal is open
 * and the job is in a non-terminal state.
 */
export function useDocumentSetKgSyncJob(
  setId: string | undefined,
  jobId: string | undefined,
  options: { refetchInterval?: number | false } = {},
) {
  const meta = useMetaphysics();
  return useQuery({
    queryKey: ['document-set-kg-job', setId, jobId] as const,
    enabled: !!setId && !!jobId,
    refetchInterval: options.refetchInterval ?? false,
    queryFn: () =>
      meta
        .get(
          `document-sets/${encodeURIComponent(setId as string)}/kg/sync-jobs/${encodeURIComponent(jobId as string)}`,
        )
        .json<IDocumentSetKgSyncJobWithItems>(),
  });
}

/**
 * Cancel an in-flight KG sync job. Idempotent server-side — hitting a
 * terminal job returns 200 with `changed: false`. On any successful call
 * we invalidate the versions list + the specific job so the UI's
 * "Sync running…" affordance resolves to "Cancelled" within one polling
 * tick.
 */
export function useCancelKgSyncJob(setId: string) {
  const meta = useMetaphysics();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string): Promise<{ changed: boolean }> =>
      meta
        .post(
          `document-sets/${encodeURIComponent(setId)}/kg/sync-jobs/${encodeURIComponent(jobId)}/cancel`,
        )
        .json<{ changed: boolean }>(),
    onSuccess: async (_data, jobId) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['document-set-kg-versions', setId] }),
        qc.invalidateQueries({ queryKey: ['document-set-kg-job', setId, jobId] }),
      ]);
    },
  });
}

/**
 * Generate a KG from a committed DocumentSetVersion. On success, invalidate
 * the KG versions list so the new row appears immediately; the modal then
 * starts polling the returned jobId for progress.
 */
export function useGenerateKg(setId: string) {
  const meta = useMetaphysics();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      versionId,
      body,
    }: {
      versionId: string;
      body: IGenerateKgRequest;
    }): Promise<IGenerateKgResponse> =>
      meta
        .post(
          `document-sets/${encodeURIComponent(setId)}/versions/${encodeURIComponent(versionId)}/generate-kg`,
          { json: body },
        )
        .json<IGenerateKgResponse>(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['document-set-kg-versions', setId] });
    },
  });
}

// ── Extraction schema hooks ───────────────────────────────────────────────
//
// Doc-set-scoped mirror of the pipeline `useExtractionSchema*` hooks. Every
// mutation invalidates the schemas list; `generate` + `activate` also
// invalidate the parent `document-set` query because they change
// `activeExtractionSchemaId` on the set.

export function useDocumentSetExtractionSchemas(setId: string | undefined) {
  const meta = useMetaphysics();
  return useQuery({
    queryKey: ['document-set-extraction-schemas', setId] as const,
    enabled: !!setId,
    queryFn: () =>
      meta
        .get(`document-sets/${encodeURIComponent(setId as string)}/extraction-schemas`)
        .json<{ items: IDocumentSetExtractionSchema[] }>(),
    select: (raw) => raw.items,
  });
}

export function useGenerateDocumentSetExtractionSchema(setId: string) {
  const meta = useMetaphysics();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body?: {
      customPrompt?: string;
      promptMode?: 'replace' | 'append';
    }): Promise<IDocumentSetExtractionSchema> =>
      meta
        .post(`document-sets/${encodeURIComponent(setId)}/extraction-schemas/generate`, {
          json: body ?? {},
        })
        .json<IDocumentSetExtractionSchema>(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['document-set-extraction-schemas', setId] });
      await qc.invalidateQueries({ queryKey: ['document-set', setId] });
    },
  });
}

export function usePatchDocumentSetExtractionSchema(setId: string) {
  const meta = useMetaphysics();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      schemaId,
      body,
    }: {
      schemaId: string;
      body: {
        entityTypes?: string[];
        domainContext?: string;
        domain?: string;
        isLocked?: boolean;
        generatedFrom?: IDocumentSetExtractionSchemaGeneratedFrom;
      };
    }): Promise<IDocumentSetExtractionSchema> =>
      meta
        .patch(
          `document-sets/${encodeURIComponent(setId)}/extraction-schemas/${encodeURIComponent(
            schemaId,
          )}`,
          { json: body },
        )
        .json<IDocumentSetExtractionSchema>(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['document-set-extraction-schemas', setId] });
    },
  });
}

export function useActivateDocumentSetExtractionSchema(setId: string) {
  const meta = useMetaphysics();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (schemaId: string) =>
      meta
        .post(
          `document-sets/${encodeURIComponent(setId)}/extraction-schemas/${encodeURIComponent(
            schemaId,
          )}/activate`,
        )
        .json<{ success: boolean; documentSetId: string; schemaId: string }>(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['document-set-extraction-schemas', setId] });
      await qc.invalidateQueries({ queryKey: ['document-set', setId] });
    },
  });
}

export function useDiffVersions(
  setId: string | undefined,
  from: string | undefined,
  to: string | undefined,
) {
  const meta = useMetaphysics();
  return useQuery({
    queryKey: ['document-set-diff', setId, from, to] as const,
    enabled: !!setId && !!from && !!to,
    queryFn: () =>
      meta
        .get(
          `document-sets/${encodeURIComponent(setId as string)}/versions/${encodeURIComponent(
            from as string,
          )}/diff/${encodeURIComponent(to as string)}`,
        )
        .json<IVersionDiff>(),
  });
}

export function useDocumentSetLineage(setId: string | undefined) {
  const meta = useMetaphysics();
  return useQuery({
    queryKey: ['document-set-lineage', setId] as const,
    enabled: !!setId,
    queryFn: () =>
      meta
        .get(`document-sets/${encodeURIComponent(setId as string)}/versions/latest/lineage`)
        .json<ILineageResponse>(),
    // SSE events drive immediate updates; 60s is a slow fallback.
    refetchInterval: 60_000,
  });
}

export function useEntryDetail(
  setId: string | undefined,
  entryId: string | undefined,
  enabled = true,
) {
  const meta = useMetaphysics();
  return useQuery({
    queryKey: ['document-set-entry', setId, entryId] as const,
    // `converted:…` ids are conversion artifacts projected into the lineage
    // from the conversion cache. They have no DocumentSetEntry row, so the
    // fetch would always 404 — the caller renders them from lineage instead.
    // Draft entries have no committed `latest` version yet; the caller passes
    // `enabled: false` and renders them from local draft state.
    enabled: enabled && !!setId && !!entryId && !entryId.startsWith('converted:'),
    queryFn: () =>
      meta
        .get(
          `document-sets/${encodeURIComponent(setId as string)}/versions/latest/entries/${encodeURIComponent(entryId as string)}`,
        )
        .json<IEntryDetail>(),
  });
}

export function useDocumentSetSkillRuns(setId: string | undefined) {
  const meta = useMetaphysics();
  return useQuery({
    queryKey: ['document-set-skill-runs', setId] as const,
    enabled: !!setId,
    queryFn: () =>
      meta
        .get(`document-sets/${encodeURIComponent(setId as string)}/skill-runs`)
        .json<{ items: ISkillRun[] }>(),
    select: (raw) => raw.items,
    // Poll while any run is pending/running
    refetchInterval: (query) => {
      const runs = query.state.data;
      if (!Array.isArray(runs)) return false;
      const hasActive = runs.some((r) => r.status === 'pending' || r.status === 'running');
      return hasActive ? 3_000 : false;
    },
  });
}
